import clsx from 'clsx'
import isEmpty from 'lodash/isEmpty'
import React, { FC, useCallback, useContext, useState } from 'react'

import SvgPlus from '@Assets/SVG/Plus'
import { TopLinksSlider } from '@Blocks/ProductList/Components/TopLink/Slider'
import { TopLink } from '@Blocks/ProductList/Components/TopLink/types'
import { useScrollToTop } from '@Blocks/ProductList/useScrollToTop'
import { Breadcrumbs } from '@Components/Breadcrumbs'
import { CategoryFacetsWithChips } from '@Components/Facet/Display/CategoryMenu/CategoryFilters'
import { SortingAccordion } from '@Components/Facet/Display/CategoryMenu/SortingAccordion'
import { SVGIcon } from '@Components/Icons/SVGIcon'
import {
  NativeModal,
  NativeModalBody,
  NativeModalFooter,
  NativeModalHeader,
  useNativeModalActions,
} from '@Components/Modal/NativeModal/NativeModal'
import { Pagination, PaginationArrows, PaginationShowingText } from '@Components/Pagination'
import { LinkedData } from '@Components/ProductList/LinkedData'
import { ProductListNoProducts } from '@Components/ProductList/NoProducts'
import { ProductTilesList } from '@Components/ProductList/ProductTiles/List'
import { convertRTEToText, RTEContent } from '@Components/RTEContent'
import { TextWithMoreAndLess } from '@CoreComponents/TextFields/TextWithMoreAndLess'
import { isEventDesktop } from '@Domains/Navigation/Features/Menu/Menu'
import { RteField } from '@Generated/graphql'
import { ProductSearchContext } from '@Globals/Algolia/Context'
import { AnchorItemInjections } from '@Globals/Injections/Client'
import { useTranslation } from '@Globals/Translations'

import styles from './v2.module.scss'

export interface ProductListBlockProps {
  description?: RteField | null
  enableDescription?: boolean
  enableDescriptionShowMore?: boolean
  id?: string
  topLinks: TopLink[]
}

export const ProductListBlockV2: FC<ProductListBlockProps> = ({
  description,
  enableDescription,
  enableDescriptionShowMore,
  id,
  topLinks,
}) => {
  const { t } = useTranslation()
  const [appliedFiltersOnMobile, setAppliedFiltersOnMobile] = useState(false)
  const { scrollToTop, wrapperRef } = useScrollToTop()
  const filterModalActions = useNativeModalActions()
  const { count, currentHits, page, pages, title } = useContext(ProductSearchContext)

  const onFilterChangeMobile = useCallback(() => {
    setAppliedFiltersOnMobile(true)
    scrollToTop()
  }, [scrollToTop])

  let descriptionContent = enableDescription && description
    ? <RTEContent className={styles.description} data={description} />
    : null
  if (descriptionContent && enableDescriptionShowMore) {
    descriptionContent = (
      <TextWithMoreAndLess
        className={styles.productListDescription}
        id="productListDescription"
        lessText={t('cms:SHOW_LESS_LABEL', '⌃')}
        moreText={t('cms:READ_MORE_LABEL', '⌄')}
      >
        {descriptionContent}
      </TextWithMoreAndLess>
    )
  }

  return (
    <>
      <Breadcrumbs withMaxWidth />

      <div className={styles.productListBlock} id={id}>
        <div className={styles.topLinksWrapper}>
          <div className={clsx(styles.topLinksContent, { [styles.topLinksContentFullWidth]: isEmpty(topLinks) })}>
            <header className={styles.header}>
              <h1>
                {title.page && (
                  <>
                    {title.page}
                    {' - '}
                  </>
                )}
                {title.canonical && (
                  <>
                    {title.canonical}
                    {' - '}
                  </>
                )}
                {title.base}
              </h1>
              {descriptionContent}
            </header>
          </div>
          {!isEmpty(topLinks) && (
            <div className={styles.topLinks}>
              <TopLinksSlider items={topLinks} />
            </div>
          )}
        </div>
        <p className={clsx(styles.numberOfProducts, styles.numberOfProductsMobile)}>
          {t('products:CP_X_PRODUCTS', '{{arg1}} products', { arg1: currentHits })}
        </p>
        <div className={styles.actions} ref={wrapperRef}>
          <div className={styles.sortingAndFilters}>
            <div className={styles.filterWrapper}>
              <button
                className={styles.filterButton}
                id="category-filterV2"
                onClick={(e) => {
                  if (!isEventDesktop(e)) {
                    filterModalActions.current?.open()
                  }
                }}
                type="button"
              >
                <div className={styles.filterTitleFilters}>
                  <p className={styles.filterTitleText}>
                    {t('common:FILTERS')}
                    {count > 0 && <span>{` (${count})`}</span>}
                  </p>
                  <SVGIcon icon={SvgPlus} size="20px" strokeColor="var(--neutral-900)" />
                </div>
              </button>

            </div>

            <p className={clsx(styles.numberOfProducts, styles.numberOfProductsTablet)}>
              {t('products:CP_X_PRODUCTS', '{{arg1}} products', { arg1: currentHits })}
            </p>
            <div className={styles.sorting}>
              <SortingAccordion scrollToTop={scrollToTop} />
            </div>
          </div>
          <div className={styles.productGrid}>
            <ProductTilesList scrollToTop={scrollToTop} />
            {pages > 1 && (
              <Pagination>
                <PaginationArrows onClick={scrollToTop} pages={pages} />
                <PaginationShowingText currentHits={currentHits} page={page || 1} />
              </Pagination>
            )}
            {currentHits === 0 && (
              <>
                <AnchorItemInjections anchor="no_hits.before" />
                <ProductListNoProducts />
                <AnchorItemInjections anchor="no_hits.after" />
              </>
            )}
          </div>
          <div className={styles.filtersOnDesktop}>
            <CategoryFacetsWithChips
              key="category_filter"
              className={styles.filterList}
              onFilterChange={scrollToTop}
            />
          </div>
          <div />
        </div>
        <NativeModal
          actionRef={filterModalActions}
          alignment="left"
          backdrop
          onClose={() => {
            filterModalActions.current?.close()
            if (appliedFiltersOnMobile) {
              scrollToTop()
              setAppliedFiltersOnMobile(false)
            }
          }}
        >
          <NativeModalHeader>
            {t('common:FILTERS')}
          </NativeModalHeader>
          <NativeModalBody>
            <div className={styles.filterModal}>
              <div className={styles.filterModalFilters}>
                <CategoryFacetsWithChips
                  key="category_filter"
                  className={styles.filterList}
                  mobile
                  onFilterChange={onFilterChangeMobile}
                />
              </div>
            </div>
          </NativeModalBody>
          <NativeModalFooter className={styles.footerButton}>
            <button
              className={styles.filterButtonMobil}
              onClick={(e) => {
                if (!isEventDesktop(e)) {
                  scrollToTop()
                  filterModalActions.current?.close()
                }
              }}
              type="button"
            >
              <p>
                {t('products:SHOW_RESULTS')}
                {` (${currentHits})`}
              </p>
            </button>
          </NativeModalFooter>
        </NativeModal>
        {/* JSON-LD descriptions should be plain text, so flatten the RTE field rather than render it. */}
        <LinkedData description={convertRTEToText(description)} title={[title.canonical, title.base, title.page].filter(Boolean).join(' - ')} />
      </div>
    </>
  )
}
